import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { SelectionAlwaysOnDisplay } from "@lexical/react/LexicalSelectionAlwaysOnDisplay";
import { HeadingNode } from "@lexical/rich-text";
import { ListItemNode, ListNode } from "@lexical/list";
import { LinkNode } from "@lexical/link";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import debounce from "lodash/debounce";
import classnames from "classnames";
import { Box } from "../../Box/index.js";
import { Typography } from "../../Typography/index.js";
import { Label } from "../common/index.js";
import { useLogger } from "../../services/hooks/index.js";
import { WysiwygContext } from "./context/index.js";
import { theme } from "./themes/index.js";
import { AdditionalFetchDataHelpersPlugin, CharacterLimitPlugin, MentionPlugin, PasteCleanupPlugin, SendFormPlugin } from "./plugins/index.js";
import { useStyles } from "./WysiwygInput.styles.js";
import { $isMentionNode, CodeBlockNode, FileNode, ImagesGalleryNode, MentionNode, QuoteNode, ReferenceNode, SingleImageNode, VideoNode } from "./nodes/index.js";
import { getAllTextContent, hasNodeType } from "./utils/index.js";
import { ToolbarPlugins } from "./ToolbarPlugins.js";
import { FunctionalPlugins } from "./FunctionalPlugins.js";
function _define_property(obj, key, value) {
    if (key in obj) Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
    });
    else obj[key] = value;
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = null != arguments[i] ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if ("function" == typeof Object.getOwnPropertySymbols) ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
            return Object.getOwnPropertyDescriptor(source, sym).enumerable;
        }));
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function WysiwygInput_ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) symbols = symbols.filter(function(sym) {
            return Object.getOwnPropertyDescriptor(object, sym).enumerable;
        });
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = null != source ? source : {};
    if (Object.getOwnPropertyDescriptors) Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    else WysiwygInput_ownKeys(Object(source)).forEach(function(key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
    return target;
}
const DEBOUNCE_TIMEOUT = 500;
const MAXIMUM_CHARACTERS_LIMIT = 20000;
const SHARED_EDITOR_CONFIG = {
    nodes: [
        HeadingNode,
        ListNode,
        ListItemNode,
        LinkNode,
        TableNode,
        TableRowNode,
        TableCellNode,
        ReferenceNode,
        CodeBlockNode,
        QuoteNode,
        SingleImageNode,
        ImagesGalleryNode,
        VideoNode,
        FileNode,
        MentionNode
    ],
    theme: theme,
    onError: ()=>{}
};
const WysiwygInput = ({ config, value, onChange, onBlur, onFocus, placeholder = 'Добавьте текст', label, required = false, tooltip = null, error = false, helperText = '', focusMode, debounceTimeout = DEBOUNCE_TIMEOUT, maxCharLimit = MAXIMUM_CHARACTERS_LIMIT, showCharLimitCounter = false, loadFile, getAttachmentUrl, searchMentions, onUploadError, getCurrentMediaIds, getCurrentMentionsData, checkEditorContainsMention, handleSendClick, editable = true, hideSubmitButton, bgcolor = 'bg.greyscale.primary', withoutPaddings = false, apiUrl, envVars = {
    allowDirectVideoLinks: false
}, isSubmitting = false, showErrors, initialValue, flat = false, stickyToolbar = false })=>{
    const { classes } = useStyles();
    const logger = useLogger();
    const handleEditorError = useCallback((editorError)=>{
        logger.error(editorError, {});
    }, [
        logger
    ]);
    const [mediaIds, setMediaIds] = useState([]);
    const [mentionsData, setMentionsData] = useState([]);
    const [isLimitExceeded, setIsLimitExceeded] = useState(false);
    const [includeMention, setIncludeMention] = useState(false);
    const floatingAnchorRef = useRef(null);
    const hasError = error && helperText && showErrors || isLimitExceeded;
    const wysiwygContext = useMemo(()=>({
            apiUrl: apiUrl,
            envVars: {
                allowDirectVideoLinks: envVars.allowDirectVideoLinks
            }
        }), [
        apiUrl,
        envVars.allowDirectVideoLinks
    ]);
    const onError = useCallback((e)=>{
        logger.error(e, {
            tags: {
                vkdoc_error_type: 'wysiwyg input'
            }
        });
    }, [
        logger
    ]);
    const editorConfig = useMemo(()=>_object_spread_props(_object_spread({}, SHARED_EDITOR_CONFIG), {
            onError,
            nodes: [
                ...config.plugins.formatHeadingPlugin.heading ? [
                    HeadingNode
                ] : [],
                ...config.plugins.linkAndTablePlugin.link ? [
                    LinkNode
                ] : [],
                ...config.plugins.linkAndTablePlugin.table ? [
                    TableNode,
                    TableRowNode,
                    TableCellNode
                ] : [],
                ...config.plugins.linkAndTablePlugin.mention ? [
                    MentionNode
                ] : [],
                ...config.plugins.linkAndTablePlugin.reference ? [
                    ReferenceNode
                ] : [],
                ...config.plugins.linkAndTablePlugin.codeBlock ? [
                    CodeBlockNode
                ] : [],
                ...config.plugins.linkAndTablePlugin.quote ? [
                    QuoteNode
                ] : [],
                ...config.plugins.listAndFormatPlugin.list ? [
                    ListNode,
                    ListItemNode
                ] : [],
                ...config.plugins.mediaPlugin.singeImage ? [
                    SingleImageNode
                ] : [],
                ...config.plugins.mediaPlugin.imagesGallery ? [
                    ImagesGalleryNode
                ] : [],
                ...config.plugins.mediaPlugin.video ? [
                    VideoNode
                ] : [],
                ...config.plugins.mediaPlugin.file ? [
                    FileNode
                ] : []
            ]
        }), [
        config.plugins,
        onError
    ]);
    useEffect(()=>{
        if (checkEditorContainsMention) checkEditorContainsMention(includeMention);
    }, [
        includeMention,
        checkEditorContainsMention
    ]);
    useEffect(()=>{
        if (getCurrentMediaIds) getCurrentMediaIds(mediaIds);
    }, [
        mediaIds,
        getCurrentMediaIds
    ]);
    useEffect(()=>{
        if (getCurrentMentionsData) getCurrentMentionsData(mentionsData);
    }, [
        mentionsData,
        getCurrentMentionsData
    ]);
    const initialConfig = useMemo(()=>_object_spread_props(_object_spread({}, editorConfig), {
            namespace: 'WysiwigInput',
            editorState: (editor)=>{
                if (initialValue) return void editor.setEditorState(editor.parseEditorState(initialValue));
                if (null == value ? void 0 : value.data) editor.setEditorState(editor.parseEditorState(value.data));
            },
            editable,
            onError: handleEditorError
        }), [
        editorConfig,
        value.data,
        editable,
        initialValue,
        handleEditorError
    ]);
    const debouncedOnChange = useCallback(()=>debounce((editorState)=>{
            let textContent = '';
            let hasContent = false;
            editorState.read(()=>{
                textContent = getAllTextContent(editorState);
            });
            const remainingChars = maxCharLimit - textContent.length;
            const charLimitAvailable = remainingChars >= 0;
            const hasTextContent = Boolean(textContent);
            const limitExceeded = !charLimitAvailable;
            setIsLimitExceeded(()=>limitExceeded);
            hasContent = charLimitAvailable && hasTextContent;
            if (charLimitAvailable && !hasContent) hasContent = hasNodeType(editorState, 'file') || hasNodeType(editorState, 'single-image') || hasNodeType(editorState, 'images-gallery') || hasNodeType(editorState, 'video') || hasNodeType(editorState, 'mention-node');
            const mentionNodeFound = hasNodeType(editorState, 'mention-node', $isMentionNode);
            setIncludeMention(()=>mentionNodeFound);
            if (limitExceeded) hasContent = false;
            onChange({
                data: editorState.toJSON(),
                hasContent,
                isLimitExceeded: limitExceeded
            });
        }, debounceTimeout), [
        debounceTimeout,
        maxCharLimit,
        onChange
    ])();
    return /*#__PURE__*/ jsx(WysiwygContext.Provider, {
        value: wysiwygContext,
        children: /*#__PURE__*/ jsxs(LexicalComposer, {
            initialConfig: initialConfig,
            children: [
                /*#__PURE__*/ jsxs(AdditionalFetchDataHelpersPlugin, {
                    getAttachmentUrl: getAttachmentUrl,
                    searchMentions: searchMentions,
                    children: [
                        /*#__PURE__*/ jsxs(Box, _object_spread_props(_object_spread({
                            p: withoutPaddings ? 'unset' : '24'
                        }, stickyToolbar && {
                            pt: '0'
                        }), {
                            bgcolor: bgcolor,
                            radius: "l",
                            position: "relative",
                            className: classnames(hasError && !flat && classes.error, flat && classes.flatRoot),
                            children: [
                                /*#__PURE__*/ jsx(ToolbarPlugins, {
                                    config: config,
                                    editable: editable,
                                    loadFile: loadFile,
                                    onUploadError: onUploadError,
                                    bgcolor: bgcolor,
                                    classes: classes,
                                    floatingAnchorElem: floatingAnchorRef.current,
                                    stickyToolbar: stickyToolbar
                                }),
                                /*#__PURE__*/ jsx(FunctionalPlugins, {
                                    config: config,
                                    debouncedOnChange: debouncedOnChange,
                                    focusMode: focusMode,
                                    setMediaIds: setMediaIds,
                                    setMentionsData: setMentionsData
                                }),
                                /*#__PURE__*/ jsx(MentionPlugin, {
                                    mention: config.plugins.linkAndTablePlugin.mention,
                                    children: /*#__PURE__*/ jsx(RichTextPlugin, {
                                        contentEditable: /*#__PURE__*/ jsxs(Fragment, {
                                            children: [
                                                label && /*#__PURE__*/ jsx(Label, {
                                                    label: label,
                                                    required: required,
                                                    tooltip: tooltip
                                                }),
                                                /*#__PURE__*/ jsxs(Box, {
                                                    display: "flex",
                                                    alignItems: "flex-end",
                                                    flexWrap: "nowrap",
                                                    position: "relative",
                                                    ref: floatingAnchorRef,
                                                    children: [
                                                        /*#__PURE__*/ jsx(Box, {
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            width: "100%",
                                                            children: /*#__PURE__*/ jsx(ContentEditable, {
                                                                id: "lexicalContentEditable",
                                                                className: 'comments' === config.mode ? classnames([
                                                                    classes.root,
                                                                    classes.commtensRoot,
                                                                    hasError && classes.error,
                                                                    !editable && classes.disabled
                                                                ]) : classnames([
                                                                    classes.root,
                                                                    hasError && classes.error,
                                                                    !editable && classes.disabled
                                                                ]),
                                                                "aria-placeholder": placeholder,
                                                                placeholder: /*#__PURE__*/ jsx(Box, {
                                                                    pointerEvents: "none",
                                                                    className: classes.placeholder,
                                                                    children: /*#__PURE__*/ jsx(Typography, {
                                                                        variant: "body2",
                                                                        color: "text.light.tertirary",
                                                                        children: placeholder
                                                                    })
                                                                }),
                                                                onBlur: onBlur,
                                                                onFocus: onFocus,
                                                                value: JSON.stringify(value.data)
                                                            })
                                                        }),
                                                        'comments' === config.mode && !hideSubmitButton && /*#__PURE__*/ jsx(SendFormPlugin, {
                                                            hasContent: value.hasContent,
                                                            handleSendClick: handleSendClick,
                                                            isSubmitting: isSubmitting
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        ErrorBoundary: LexicalErrorBoundary
                                    })
                                }),
                                isLimitExceeded && !showCharLimitCounter && /*#__PURE__*/ jsx(Box, {
                                    children: /*#__PURE__*/ jsx(Typography, {
                                        variant: "caption",
                                        color: flat ? 'accent.text.error' : 'text.light.secondary',
                                        children: `Введено максимальное количество символов: ${String(maxCharLimit).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`
                                    })
                                }),
                                showCharLimitCounter && /*#__PURE__*/ jsx(Box, {
                                    mt: "4",
                                    children: /*#__PURE__*/ jsx(CharacterLimitPlugin, {
                                        limit: maxCharLimit,
                                        descriptionComponent: helperText ? /*#__PURE__*/ jsx(Typography, {
                                            variant: "caption",
                                            color: "accent.text.error",
                                            children: helperText
                                        }) : null
                                    })
                                }),
                                hasError && !showCharLimitCounter && /*#__PURE__*/ jsx(Typography, {
                                    variant: "caption",
                                    color: "accent.text.error",
                                    children: helperText
                                })
                            ]
                        })),
                        /*#__PURE__*/ jsx(SelectionAlwaysOnDisplay, {})
                    ]
                }),
                /*#__PURE__*/ jsx(PasteCleanupPlugin, {})
            ]
        })
    });
};
export { SHARED_EDITOR_CONFIG, WysiwygInput };
